/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
import { useEffect, useState } from 'react';
import { getEmbedParams } from './powerbidata';

type Props = {
  data: any;
  block_id: any;
  editable: boolean;
};

const loadReport = async (
  config,
  block_id,
  pageName,
  visualName,
  editable,
  setPages,
  setVisuals,
) => {
  if (!config.reportConfig || !block_id) {
    // maybe an empty block
    return;
  }
  // We need to import powerbi-client here instead of at the top
  // to avoid 'ReferenceError: self is not defined'.
  const pbi = await import('powerbi-client');

  // We need to define a powerbi service.  Done in the way that the React example does:
  // https://github.com/microsoft/PowerBI-Developer-Samples/blob/master/React-TS/Embed%20for%20your%20organization/UserOwnsData/src/App.tsx#L13
  const powerbi = new pbi.service.Service(
    pbi.factories.hpmFactory,
    pbi.factories.wpmpFactory,
    pbi.factories.routerFactory,
  );

  // The rest of the code follows the JavaScript part of the Python example:
  // https://github.com/microsoft/PowerBI-Developer-Samples/blob/master/Python/Embed%20for%20your%20customers/AppOwnsData/static/js/index.js
  let report_type = '';
  if (visualName) {
    report_type = 'visual';
  } else if (config.reportConfig?.reportType === 'PowerBIReport') {
    report_type = 'report';
  } else {
    console.warn(
      'Unknown reportType in embed config: ' + config.reportConfig?.reportType,
    );
    return;
  }
  // Initialize iframe for embedding report
  const container = document.querySelector('#report-container-' + block_id);
  if (!container) {
    return;
  }
  powerbi.bootstrap(container, { type: report_type });
  const reportLoadConfig = {
    type: report_type,
    tokenType: pbi.models.TokenType.Embed,
    accessToken: config.token,
    embedUrl: config.reportConfig.embedUrl,

    // Enable this setting to remove gray shoulders from embedded report
    // settings: {
    //     background: pbi.models.BackgroundType.Transparent
    // }
  };
  if (pageName) {
    reportLoadConfig.pageName = pageName;
  }
  if (visualName) {
    reportLoadConfig.visualName = visualName;
  }

  // Maybe refresh the token, otherwise the user may not be able to
  // interact with the report if they are unlucky enough that the access token
  // expires shortly after loading the report.
  // But the embed token is fresh, and is valid for one hour.
  // https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/refresh-token

  // Load the report or page or visual
  const report = powerbi.load(container, reportLoadConfig);

  // Triggers when a report schema is successfully loaded
  report.on('loaded', function () {
    if (!editable) {
      // We only want to do something extra when editing.
      return;
    }

    // Get a list of pages in the report.
    report
      .getPages()
      .then((r) => {
        setPages(r);
      })
      .catch((err) => console.error(err));

    if (pageName) {
      // Get a list of visuals on the chosen page.
      report
        .getPageByName(pageName)
        .then((page) => {
          page
            .getVisuals()
            .then((visuals) => {
              setVisuals(visuals);
            })
            .catch((err) => console.error(err));
        })
        .catch((err) => console.error(err));
    }
  });

  // Triggers when a report is successfully embedded in UI
  report.on('rendered', function () {
    // By default, the iframe gets a height of 220px,
    // because we have set this as min-height for all iframes.
    // Without this, I guess it may have been even smaller.
    // There might be a nicer way to do this,
    // but let's hardcode the height for now.
    report.iframe.style.height = '800px';
  });

  // Clear any other error handler event
  report.off('error');
  // let page = await bootstrapped.getPageByName(pageName).then((r) => {

  // Below patch of code is for handling errors that occur during embedding
  report.on('error', function (event) {
    var errorMsg = event.detail;

    // Use errorMsg variable to log error in any destination of choice
    console.error(errorMsg);
    return;
  });
};

const PowerBI = (props: Props) => {
  const [config, setConfig] = useState({});
  const [pages, setPages] = useState([]);
  const [visuals, setVisuals] = useState([]);
  const data = props.data;
  const block_id = props.block_id;
  const url = data?.url;
  const pageName = data?.pageName;
  const visualName = data?.visualName;

  useEffect(() => {
    getEmbedParams(url, pageName, visualName).then(setConfig);
  }, [url, pageName, visualName, setConfig]);

  // This is only used when editing:
  const page_list = pages.map((page, index) => (
    <div key={index}>
      <span>{page.displayName}</span>: <code>{page.name}</code>
    </div>
  ));
  const visual_list = visuals.map((visual, index) => (
    <div key={index}>
      <span>
        {visual.type} {visual.title}
      </span>
      : <code>{visual.name}</code>
    </div>
  ));

  return (
    <section>
      <h2>{config.reportConfig?.name || 'PowerBI block'}</h2>
      {page_list.length > 0 && (
        <details>
          <summary>Pages</summary>
          {page_list}
        </details>
      )}
      {visual_list.length > 0 && (
        <details>
          <summary>Visuals</summary>
          {visual_list}
        </details>
      )}
      <div className="report-container" id={'report-container-' + block_id}>
        <iframe
          title={config.reportConfig?.name}
          src={config.reportConfig?.embedUrl}
          onLoad={() =>
            loadReport(
              config,
              block_id,
              pageName,
              visualName,
              props.editable,
              setPages,
              setVisuals,
            )
          }
        />
      </div>
    </section>
  );
};

export default PowerBI;
